import { useEffect, useState } from 'react';
import { endpoints, getSession, login, logout, type Session } from './api';
import { Button, Field, Note, currency } from './components';
import { useApiQuery } from './hooks';
import { AdminPage } from './pages/admin';
import { BlueprintsPage, IndustryPage, InventoryPage, JobsPage, MarketPage } from './pages/core';
import { TodayPage } from './pages/today';
import { SynchronizationPage } from './pages/synchronization';
import { FinancePage } from './pages/finance';
import { BusinessPage, LedgerPage } from './pages/business';
import { MarketManagementPage } from './pages/market-management';
import { KillmailsPage } from './pages/killmails';
import { InventionPage } from './pages/invention';
import { MiningPage } from './pages/mining';
import { WorkflowPage } from './pages/workflow';
import { AnoikisPage, ExpeditionsPage, SkillsPage, WormholesPage } from './pages/operations';
import { PiPage } from './pages/planetary';

type RouteItem = { id: string; label: string; component: () => React.JSX.Element };

const groups: Array<{ label: string; pages: RouteItem[] }> = [
  { label: '', pages: [{ id: 'overview', label: 'Resumen', component: TodayPage }] },
  { label: 'Industria', pages: [
    { id: 'market', label: 'Oportunidades', component: MarketPage },
    { id: 'industry', label: 'Calculadora', component: IndustryPage },
    { id: 'workflow', label: 'Workflow', component: WorkflowPage },
    { id: 'industry/jobs', label: 'Jobs', component: JobsPage },
    { id: 'inventory', label: 'Materiales', component: InventoryPage },
    { id: 'mining', label: 'Minería', component: MiningPage },
    { id: 'blueprints', label: 'Blueprints', component: BlueprintsPage },
    { id: 'invention', label: 'Invención', component: InventionPage },
    { id: 'skills', label: 'Skills', component: SkillsPage },
    { id: 'pi', label: 'PI', component: PiPage },
  ] },
  { label: 'ISK', pages: [
    { id: 'trading', label: 'Órdenes', component: MarketManagementPage },
    { id: 'finance', label: 'Negocio', component: BusinessPage },
    { id: 'ledger', label: 'Ledger', component: LedgerPage },
    { id: 'finance-records', label: 'Costes y lotes', component: FinancePage },
  ] },
  { label: 'Espacio', pages: [
    { id: 'expeditions', label: 'Expediciones', component: ExpeditionsPage },
    { id: 'killmails', label: 'Killmails', component: KillmailsPage },
    { id: 'wormholes', label: 'Wormholes', component: WormholesPage },
    { id: 'anoikis', label: 'Anoikis', component: AnoikisPage },
  ] },
  { label: 'Sistema', pages: [
    { id: 'sync', label: 'Sincronización', component: SynchronizationPage },
    { id: 'admin', label: 'Administración', component: AdminPage },
  ] },
];
const routes = groups.flatMap((group) => group.pages);

function readRoute() {
  const path = window.location.hash.replace('#/', '').split('?')[0];
  if (path === 'finance/batches' || path.startsWith('finance/batches/')) return 'finance-records';
  if (path === 'industry/jobs') return path;
  const id = path.split('/')[0];
  return routes.some((route) => route.id === id) ? id : 'overview';
}

export default function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [sessionError, setSessionError] = useState<string | null>(null);

  useEffect(() => {
    getSession().then(setSession).catch((error: Error) => setSessionError(error.message));
  }, []);

  if (sessionError) return <BackendUnavailable message={sessionError} retry={() => { setSessionError(null); getSession().then(setSession).catch((error: Error) => setSessionError(error.message)); }} />;
  if (!session) return <div className="gate"><p className="muted" role="status">Cargando…</p></div>;
  if (!session.authenticated) return <LoginScreen onLogin={setSession} />;
  const onLogout = () => setSession({ authenticated: false, csrfToken: null, isAdmin: false, characterId: null, canUseApp: false, corporationName: session.corporationName });
  if (!session.canUseApp) return <SetupShell session={session} onLogout={onLogout} />;
  return <Shell session={session} onLogout={onLogout} />;
}

function SetupShell({ session, onLogout }: { session: Session; onLogout: () => void }) {
  useEffect(() => { document.title = 'Administración · Nexo'; }, []);
  return <div className="shell">
    <aside className="side">
      <div className="side-top"><a className="wordmark" href="#/admin">nexo</a></div>
      <div className="side-body is-open">
        <nav aria-label="Secciones"><div className="nav-group"><h2>Sistema</h2><a href="#/admin" aria-current="page">Administración</a></div></nav>
        <div className="side-foot"><button type="button" onClick={async () => { await logout(); onLogout(); }}>Salir</button></div>
      </div>
    </aside>
    <main><AdminPage isAdmin={session.isAdmin} canUseApp={false} /></main>
  </div>;
}

function Shell({ session, onLogout }: { session: Session; onLogout: () => void }) {
  const [route, setRoute] = useState(readRoute);
  const [pageLocation, setPageLocation] = useState(() => window.location.hash);
  const [menuOpen, setMenuOpen] = useState(false);
  const overview = useApiQuery(endpoints.overview);

  useEffect(() => {
    const onHash = () => { setRoute(readRoute()); setPageLocation(window.location.hash); window.scrollTo({ top: 0 }); };
    window.addEventListener('hashchange', onHash);
    const timer = window.setInterval(() => { if (document.visibilityState === 'visible') void overview.reload(); }, 60000);
    return () => { window.removeEventListener('hashchange', onHash); window.clearInterval(timer); };
  }, [overview.reload]);

  const selected = routes.find((page) => page.id === route) ?? routes[0];
  const Page = selected.component;
  useEffect(() => { document.title = selected.id === 'overview' ? 'Nexo' : `${selected.label} · Nexo`; setMenuOpen(false); }, [selected]);

  const ready = overview.data?.industryJobsReady ?? 0;
  const wallet = overview.data?.walletBalance;
  return <div className="shell">
    <aside className="side">
      <div className="side-top">
        <button type="button" className="menu-toggle" aria-expanded={menuOpen} onClick={() => setMenuOpen((open) => !open)}>Menú{ready > 0 ? ` · ${ready}` : ''}</button>
        <a className="wordmark" href="#/overview">nexo</a>
      </div>
      <div className={menuOpen ? 'side-body is-open' : 'side-body'}>
        <nav aria-label="Secciones">
          {groups.map((group) => <div className="nav-group" key={group.label || 'home'}>
            {group.label && <h2>{group.label}</h2>}
            {group.pages.map((page) => <a key={page.id} href={`#/${page.id}`} aria-current={page === selected ? 'page' : undefined}>
              {page.label}
              {page.id === 'industry/jobs' && ready > 0 && <span className="nav-count">{ready}</span>}
            </a>)}
          </div>)}
        </nav>
        <div className="side-foot">
          {wallet != null && <a className="wallet" href="#/finance" title="Último saldo recibido de EVE">{currency(wallet)}</a>}
          <button type="button" onClick={async () => { await logout(); onLogout(); }}>Salir</button>
        </div>
      </div>
    </aside>
    <main>{selected.id === 'admin' ? <AdminPage key={pageLocation} isAdmin={session.isAdmin} /> : <Page key={pageLocation} />}</main>
  </div>;
}

function LoginScreen({ onLogin }: { onLogin: (session: Session) => void }) {
  const eve = useApiQuery(endpoints.eveLoginStatus);
  const [operatorAccess, setOperatorAccess] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const showOperator = operatorAccess || (eve.data && !eve.data.loginAvailable);
  async function enterWithEve() {
    setBusy(true); setError(null);
    try { const result = await endpoints.startEveLogin(); window.location.assign(result.authorizeUrl); }
    catch (caught) { setError(caught instanceof Error ? caught.message : 'No se pudo iniciar EVE SSO'); setBusy(false); }
  }
  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault(); const form = new FormData(event.currentTarget); setBusy(true); setError(null);
    try { onLogin(await login(String(form.get('password')))); }
    catch (caught) { setError(caught instanceof Error ? caught.message : 'No se pudo iniciar sesión'); }
    finally { setBusy(false); }
  }
  return <div className="gate"><section className="gate-box">
    <h1>nexo</h1>
    <p>Solo miembros de Kharvex Armaments. Cada piloto tiene su espacio y sus datos personales separados.</p>
    <Button type="button" disabled={busy || !eve.data?.loginAvailable} onClick={() => void enterWithEve()}>{busy && !showOperator ? 'Conectando con CCP…' : 'Entrar con EVE Online'}</Button>
    {eve.loading && <p className="muted" role="status">Comprobando EVE SSO…</p>}
    {eve.error && <Note tone="error">No se pudo comprobar EVE SSO. <button type="button" className="link" onClick={() => void eve.reload()}>Reintentar</button></Note>}
    {eve.data && !eve.data.loginAvailable && <Note>{eve.data.configured ? 'Falta configurar la corporación admitida. Entra como operador para completar la configuración de acceso.' : 'EVE SSO no está configurado. Entra como operador para registrar el Client ID de CCP y configurar el acceso de la corporación.'}</Note>}
    {error && <Note tone="error">{error}</Note>}
    <p>La autenticación se completa en CCP. Aquí nunca se pide tu contraseña de EVE.</p>
    {!showOperator && <button type="button" className="link" onClick={() => setOperatorAccess(true)}>Acceso de operador</button>}
    {showOperator && <form className="form-stack" onSubmit={submit}>
      <Field label="Contraseña de operador" hint="Solo para la configuración inicial y la recuperación del acceso."><input required name="password" type="password" autoComplete="current-password" /></Field>
      <Button variant="secondary" disabled={busy}>{busy ? 'Verificando…' : 'Entrar como operador'}</Button>
    </form>}
  </section></div>;
}

function BackendUnavailable({ message, retry }: { message: string; retry: () => void }) {
  return <div className="gate"><section className="gate-box"><h1>nexo</h1><Note tone="error"><strong>El servidor no responde.</strong> {message}</Note><Button variant="secondary" onClick={retry}>Reintentar</Button></section></div>;
}
